import { SelectorUtil } from './selector-util';
import { SectionType } from '../models/section.model';

export interface DomHierarchy {
    element?: HTMLElement;
    column?: HTMLElement;
    row?: HTMLElement;
    mesh?: HTMLElement;
    section?: HTMLElement;
}

export class DomQuery {
    public static getRow(id: string, root: ParentNode = document): HTMLElement | null {
        return root.querySelector<HTMLElement>(`#${CSS.escape(id)}`);
    }

    public static getColumn(id: string, root: ParentNode = document): HTMLElement | null {
        return root.querySelector<HTMLElement>(`#${CSS.escape(id)}`);
    }

    public static getSections(container: HTMLElement): HTMLElement[] {
        return Array.from(container.querySelectorAll<HTMLElement>(SelectorUtil.section.base));
    }

    public static getMeshes(section: HTMLElement): HTMLElement[] {
        return Array.from(section.querySelectorAll<HTMLElement>(SelectorUtil.mesh.base));
    }

    public static getMeshContainer(mesh: HTMLElement): HTMLElement | null {
        return mesh.querySelector<HTMLElement>(`[${SelectorUtil.meshContainerAttr}]`);
    }

    public static getRows(mesh: HTMLElement): HTMLElement[] {
        return Array.from(mesh.querySelectorAll<HTMLElement>(SelectorUtil.row.base));
    }

    public static getColumns(row: HTMLElement): HTMLElement[] {
        return Array.from(row.querySelectorAll<HTMLElement>(SelectorUtil.column.base));
    }

    public static getElements(column: HTMLElement): HTMLElement[] {
        return Array.from(column.querySelectorAll<HTMLElement>(SelectorUtil.builderElement.base));
    }

    public static getParentColumn(element: HTMLElement): HTMLElement | null {
        return element.closest<HTMLElement>(SelectorUtil.column.base);
    }

    public static getParentRow(element: HTMLElement): HTMLElement | null {
        return element.closest<HTMLElement>(SelectorUtil.row.base);
    }

    public static getParentMesh(element: HTMLElement): HTMLElement | null {
        return element.closest<HTMLElement>(SelectorUtil.mesh.base);
    }

    public static getParentSection(element: HTMLElement): HTMLElement | null {
        return element.closest<HTMLElement>(SelectorUtil.section.base);
    }

    public static isColumnEmpty(column: HTMLElement): boolean {
        const elements = DomQuery.getElements(column);
        if (elements.length === 0) {
            return true;
        }

        return elements.every(el => el.style.display === 'none');
    }

    public static getColumnSize(column: HTMLElement): number {
        const colSize = column.dataset['colSize'];
        return colSize ? parseInt(colSize, 10) : 12;
    }

    public static isRowEmpty(row: HTMLElement): boolean {
        const columns = DomQuery.getColumns(row);
        if (columns.length === 0) {
            return true;
        }
        return columns.every(col => DomQuery.isColumnEmpty(col));
    }

    public static isMeshEmpty(mesh: HTMLElement): boolean {
        const rows = DomQuery.getRows(mesh);
        if (rows.length === 0) {
            return true;
        }
        return rows.every(row => DomQuery.isRowEmpty(row));
    }

    public static getSectionType(section: HTMLElement): SectionType | null {
        return section.getAttribute(SelectorUtil.sectionAttr) as SectionType | null;
    }

    public static isSectionEmpty(section: HTMLElement): boolean {
        const meshes = DomQuery.getMeshes(section);
        if (meshes.length === 0) {
            return true;
        }

        return meshes.every(mesh => DomQuery.isMeshEmpty(mesh));
    }

    public static isHiddenElement(element: HTMLElement): boolean {
        return element.getAttribute(SelectorUtil.elementAttr) === 'hidden';
    }

    public static updateFirstLastColumnClasses(row: HTMLElement): void {
        const columns = DomQuery.getColumns(row);
        columns.forEach(col => col.classList.remove('first-col', 'last-col'));

        if (columns.length > 0) {
            columns[0].classList.add('first-col');
            columns[columns.length - 1].classList.add('last-col');
        }
    }

    public static getHierarchy(id: string, root: ParentNode = document): DomHierarchy {
        const startElement = root.querySelector<HTMLElement>(`#${CSS.escape(id)}`);
        if (!startElement) {
            return {};
        }
        return DomQuery.getHierarchyFromElement(startElement);
    }

    public static getHierarchyFromElement(element: HTMLElement): DomHierarchy {
        const result: DomHierarchy = {};

        const isBuilderElement = element.hasAttribute(SelectorUtil.elementAttr);
        const isColumn = element.hasAttribute(SelectorUtil.columnAttr);
        const isRow = element.hasAttribute(SelectorUtil.rowAttr);
        const isMesh = element.hasAttribute(SelectorUtil.meshAttr);
        const isSection = element.hasAttribute(SelectorUtil.sectionAttr);

        if (isBuilderElement && DomQuery.isHiddenElement(element)) {
            result.element = element;
            return result;
        }

        if (isBuilderElement) {
            result.element = element;
            result.column = DomQuery.getParentColumn(element) ?? undefined;
            result.row = result.column ? DomQuery.getParentRow(result.column) ?? undefined : undefined;
            result.mesh = result.row ? DomQuery.getParentMesh(result.row) ?? undefined : undefined;
            result.section = result.mesh ? DomQuery.getParentSection(result.mesh) ?? undefined : undefined;
        } else if (isColumn) {
            result.column = element;
            result.row = DomQuery.getParentRow(element) ?? undefined;
            result.mesh = result.row ? DomQuery.getParentMesh(result.row) ?? undefined : undefined;
            result.section = result.mesh ? DomQuery.getParentSection(result.mesh) ?? undefined : undefined;
        } else if (isRow) {
            result.row = element;
            result.mesh = DomQuery.getParentMesh(element) ?? undefined;
            result.section = result.mesh ? DomQuery.getParentSection(result.mesh) ?? undefined : undefined;
        } else if (isMesh) {
            result.mesh = element;
            result.section = DomQuery.getParentSection(element) ?? undefined;
        } else if (isSection) {
            result.section = element;
        }

        return result;
    }
}
